import { CHUNK_SIZE, TileType, UNDERGROUND_LADDER_BOTTOM, UNDERGROUND_ROOM_Y1, SPAWN_CENTER_X } from './constants';

export interface PortalStructureContext {
    seed: number;
    runeData: Map<string, number>;
    [key: string]: any;
}

/** Move-only extraction of WorldData.ensureCentralPortalRune(). */
export function ensureCentralPortalRuneStructure(
ctx: PortalStructureContext,
calculatePortalSurfaceYFn: (seed: number) => number,
calculateSurfaceYFn: (worldX: number, seed: number) => number,
): { x: number; y: number } | null {
    try {
        console.log(`[WORLD] ensureCentralPortalRune() called`);

        const surfaceY = calculatePortalSurfaceYFn(ctx.seed);
        console.log(`[WORLD] Calculated portal surface Y=${surfaceY} from terrain formula`);

        const sc = SPAWN_CENTER_X;
        let changed = false;

        // Ensure the 11-wide stone platform at portalSurfaceY
        for (let dx = -5; dx <= 5; dx++) {
            const px = sc + dx;
            // Ensure the chunk exists for this column
            const cx = Math.floor(px / CHUNK_SIZE);
            const cy = Math.floor(surfaceY / CHUNK_SIZE);
            ctx.getChunk(cx, cy);

            const currentTile = ctx.getTile(px, surfaceY);
            // Platform: alternating turquoise/amethyst
            const dist = Math.abs(dx);
            const expected = dist % 2 === 0 ? TileType.TURQUOISE : TileType.AMETHYST;
            if (currentTile !== expected) {
                ctx.setTile(px, surfaceY, expected);
                changed = true;
            }
        }

        // Clear old rune at sc+0 on roof level (migrated to sc+1)
        {
            const oldRuneKey = `${sc},${surfaceY - 7}`;
            if (ctx.runeData.has(oldRuneKey)) {
                console.log(`[WORLD] Clearing stale rune data at (${sc}, ${surfaceY - 7})`);
                ctx.runeData.delete(oldRuneKey);
                changed = true;
            }
        }

        // Clear old portal frame tiles at sc+0 (migrated to sc+1)
        for (const oldPy of [surfaceY - 8, surfaceY - 9]) {
            const opcx = Math.floor(sc / CHUNK_SIZE);
            const opcy = Math.floor(oldPy / CHUNK_SIZE);
            ctx.getChunk(opcx, opcy);
            if (ctx.getTile(sc, oldPy) === TileType.PORTAL) {
                console.log(`[WORLD] Clearing old portal tile at (${sc}, ${oldPy})`);
                ctx.setTile(sc, oldPy, TileType.SKY);
                changed = true;
            }
        }

        // Place rune at sc+1 on the roof level (surfaceY-7)
        const runeX = sc + 1;
        {
            const rcx = Math.floor(runeX / CHUNK_SIZE);
            const rcy = Math.floor((surfaceY - 7) / CHUNK_SIZE);
            ctx.getChunk(rcx, rcy);
            const runeKey = `${runeX},${surfaceY - 7}`;
            if (ctx.getTile(runeX, surfaceY - 7) !== TileType.RUNE || ctx.runeData.get(runeKey) !== 0) {
                console.log(`[WORLD] Placing rune at (${runeX}, ${surfaceY - 7})`);
                ctx.setTile(runeX, surfaceY - 7, TileType.RUNE);
                ctx.setRuneHieroglyph(runeX, surfaceY - 7, 0);
                changed = true;
            }
        }

        // Place portal frame tiles (2 tiles above rune at sc+1)
        for (const py of [surfaceY - 8, surfaceY - 9]) {
            const pcx = Math.floor(runeX / CHUNK_SIZE);
            const pcy = Math.floor(py / CHUNK_SIZE);
            ctx.getChunk(pcx, pcy);
            if (ctx.getTile(runeX, py) !== TileType.PORTAL) {
                console.log(`[WORLD] Placing portal frame at (${runeX}, ${py})`);
                ctx.setTile(runeX, py, TileType.PORTAL);
                changed = true;
            }
        }

        // Ensure pillars at x=sc±5: 2 trunk + 5 opal, plus opal roof
        for (const dx of [-5, 5]) {
            const px = sc + dx;
            // Ensure chunks exist for pillar columns
            for (let dy = 1; dy <= 7; dy++) {
                const pillarY = surfaceY - dy;
                const pcx = Math.floor(px / CHUNK_SIZE);
                const pcy = Math.floor(pillarY / CHUNK_SIZE);
                ctx.getChunk(pcx, pcy);
            }
            // 2 trunk blocks (surfaceY-1 and surfaceY-2)
            for (let dy = 1; dy <= 2; dy++) {
                if (ctx.getTile(px, surfaceY - dy) !== TileType.TRUNK) {
                    ctx.setTile(px, surfaceY - dy, TileType.TRUNK);
                    changed = true;
                }
            }
            // 5 opal blocks (surfaceY-3 through surfaceY-7)
            for (let dy = 3; dy <= 7; dy++) {
                if (ctx.getTile(px, surfaceY - dy) !== TileType.OPAL) {
                    ctx.setTile(px, surfaceY - dy, TileType.OPAL);
                    changed = true;
                }
            }
        }
        // Continue alternating tile pattern down edge columns at sc±5
        for (const dx of [-5, 5]) {
            const px = sc + dx;
            const edgeNatSurfaceY = calculateSurfaceYFn(px, ctx.seed);
            const dist = Math.abs(dx);
            const pcx = Math.floor(px / CHUNK_SIZE);
            // Replace air, grass, dirt below platform until stone (capped at underground room)
            for (let dy = 1; surfaceY + dy < edgeNatSurfaceY + 4 && surfaceY + dy < UNDERGROUND_ROOM_Y1; dy++) {
                const ty = surfaceY + dy;
                const tcy = Math.floor(ty / CHUNK_SIZE);
                ctx.getChunk(pcx, tcy);
                const expected = (dist + dy) % 2 === 0 ? TileType.TURQUOISE : TileType.AMETHYST;
                if (ctx.getTile(px, ty) !== expected) {
                    ctx.setTile(px, ty, expected);
                    changed = true;
                }
            }
        }

        // Fill interior below platform (sc-4..sc+4) with stone down to natural stone (capped at underground room)
        for (let dx = -4; dx <= 4; dx++) {
            const fx = sc + dx;
            const fNatSurfaceY = calculateSurfaceYFn(fx, ctx.seed);
            const fcx = Math.floor(fx / CHUNK_SIZE);
            for (let dy = 1; surfaceY + dy < fNatSurfaceY + 4 && surfaceY + dy < UNDERGROUND_ROOM_Y1; dy++) {
                const fy = surfaceY + dy;
                const fcy = Math.floor(fy / CHUNK_SIZE);
                ctx.getChunk(fcx, fcy);
                if (ctx.getTile(fx, fy) !== TileType.STONE) {
                    ctx.setTile(fx, fy, TileType.STONE);
                    changed = true;
                }
            }
        }

        // Upper opal columns at sc-1 and sc+5 (surfaceY-8 through surfaceY-13)
        for (const dx of [-1, 5]) {
            const ux = sc + dx;
            const ucx = Math.floor(ux / CHUNK_SIZE);
            for (let dy = 8; dy <= 13; dy++) {
                const uy = surfaceY - dy;
                const ucy = Math.floor(uy / CHUNK_SIZE);
                ctx.getChunk(ucx, ucy);
                if (ctx.getTile(ux, uy) !== TileType.OPAL) {
                    ctx.setTile(ux, uy, TileType.OPAL);
                    changed = true;
                }
            }
        }

        // Upper alternating roof from sc-1 to sc+5 at surfaceY-14
        for (let dx = -1; dx <= 5; dx++) {
            const rx = sc + dx;
            const rcx = Math.floor(rx / CHUNK_SIZE);
            const rcy = Math.floor((surfaceY - 14) / CHUNK_SIZE);
            ctx.getChunk(rcx, rcy);
            const dist = Math.abs(dx);
            const expected = dist % 2 === 0 ? TileType.TURQUOISE : TileType.AMETHYST;
            if (ctx.getTile(rx, surfaceY - 14) !== expected) {
                ctx.setTile(rx, surfaceY - 14, expected);
                changed = true;
            }
        }

        // Ladder at sc+4 from surfaceY-7 down into underground room
        const ladderX = sc + 4;
        {
            const lcx = Math.floor(ladderX / CHUNK_SIZE);
            for (let ly = surfaceY - 7; ly <= UNDERGROUND_LADDER_BOTTOM; ly++) {
                const lcy = Math.floor(ly / CHUNK_SIZE);
                ctx.getChunk(lcx, lcy);
                if (ctx.getTile(ladderX, ly) !== TileType.LADDER) {
                    ctx.setTile(ladderX, ly, TileType.LADDER);
                    changed = true;
                }
            }
        }

        // Alternating roof from sc-5 to sc+5 at surfaceY-7 (skip sc+1 rune, sc+4 ladder)
        for (let dx = -5; dx <= 5; dx++) {
            if (dx === 1 || dx === 4) continue;
            const rx = sc + dx;
            const rcx = Math.floor(rx / CHUNK_SIZE);
            const rcy = Math.floor((surfaceY - 7) / CHUNK_SIZE);
            ctx.getChunk(rcx, rcy);
            const dist = Math.abs(dx);
            const expected = dist % 2 === 0 ? TileType.TURQUOISE : TileType.AMETHYST;
            if (ctx.getTile(rx, surfaceY - 7) !== expected) {
                ctx.setTile(rx, surfaceY - 7, expected);
                changed = true;
            }
        }

        // Edge access columns at sc±6
        for (const dx of [-6, 6]) {
            const ex = sc + dx;
            const edgeSurfaceY = calculateSurfaceYFn(ex, ctx.seed);
            const ecx = Math.floor(ex / CHUNK_SIZE);

            // Determine natural tile type at platform level
            const natAtPlatform = surfaceY < edgeSurfaceY ? TileType.SKY
                : surfaceY === edgeSurfaceY ? TileType.GRASS
                : surfaceY < edgeSurfaceY + 4 ? TileType.DIRT
                : TileType.STONE;
            const natBelow = (surfaceY + 1) < edgeSurfaceY ? TileType.SKY
                : (surfaceY + 1) === edgeSurfaceY ? TileType.GRASS
                : (surfaceY + 1) < edgeSurfaceY + 4 ? TileType.DIRT
                : TileType.STONE;

            if (natAtPlatform === TileType.SKY && natBelow === TileType.SKY) {
                // Ladder column from platform level down to ground (max 12)
                for (let dy = 0; dy < 12; dy++) {
                    const ly = surfaceY + dy;
                    if (ly >= edgeSurfaceY) break;
                    const lcy = Math.floor(ly / CHUNK_SIZE);
                    ctx.getChunk(ecx, lcy);
                    if (ctx.getTile(ex, ly) !== TileType.LADDER) {
                        ctx.setTile(ex, ly, TileType.LADDER);
                        changed = true;
                    }
                }
            } else if (natAtPlatform === TileType.DIRT) {
                // Dirt at platform level -> grass, 3 blocks above -> air
                const ecy = Math.floor(surfaceY / CHUNK_SIZE);
                ctx.getChunk(ecx, ecy);
                if (ctx.getTile(ex, surfaceY) !== TileType.GRASS) {
                    ctx.setTile(ex, surfaceY, TileType.GRASS);
                    changed = true;
                }
                for (let dy = 1; dy <= 3; dy++) {
                    const ay = surfaceY - dy;
                    const acy = Math.floor(ay / CHUNK_SIZE);
                    ctx.getChunk(ecx, acy);
                    if (ctx.getTile(ex, ay) !== TileType.SKY) {
                        ctx.setTile(ex, ay, TileType.SKY);
                        changed = true;
                    }
                }
            }
        }

        if (!changed) {
            console.log(`[WORLD] Spawn platform already correct at y=${surfaceY}`);
            return null;
        }

        console.log(`[WORLD] Spawn platform placed successfully`);
        return { x: runeX, y: surfaceY - 7 };
    } catch (error) {
        console.error(`[WORLD] ensureCentralPortalRune error:`, error);
        return null;
    }
}
